/** The Move-In board's windows, search and sort. "Current" is arriving, or arrived within the
 * month. */
export const MOVE_IN_WINDOWS = [
  { value: "current", label: "Current move-ins" },
  { value: "upcoming", label: "Moving in (future date)" },
  { value: "last30", label: "Moved in, last 30 days" },
  { value: "open", label: "Move-in not complete" },
  { value: "overdue", label: "Has overdue steps" },
  { value: "all", label: "All residents" },
] as const;
export type MoveInWindow = (typeof MOVE_IN_WINDOWS)[number]["value"];
/** The board opens on the last 30 days. */
export const DEFAULT_MOVE_IN_WINDOW: MoveInWindow = "last30";
export type MoveInSortKey = "resident" | "room" | "admission" | "pathway" | "progress" | "overdue";

export interface BoardRow {
  firstName: string;
  lastName: string;
  roomNumber: string | null;
  admissionDate: Date | string;
  current: boolean;
  moveIn: { pathway: string; complete: number; total: number; overdue: number } | null;
}

const DAY = 24 * 3600 * 1000;

export function inWindow(row: BoardRow, window: MoveInWindow, now: number): boolean {
  const admitted = new Date(row.admissionDate).getTime();
  switch (window) {
    case "current":
      return row.current;
    case "upcoming":
      return admitted > now;
    case "last30":
      return admitted <= now && admitted >= now - 30 * DAY;
    case "open":
      return Boolean(row.moveIn) && row.moveIn!.complete < row.moveIn!.total;
    case "overdue":
      return (row.moveIn?.overdue ?? 0) > 0;
    default:
      return true;
  }
}

export function matchesSearch(row: BoardRow, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  return `${row.firstName} ${row.lastName} ${row.roomNumber ?? ""}`.toLowerCase().includes(needle);
}

export function moveInSortValue(row: BoardRow, key: MoveInSortKey): string | number | null {
  switch (key) {
    case "resident":
      return `${row.lastName} ${row.firstName}`;
    case "room":
      return row.roomNumber;
    case "pathway":
      return row.moveIn?.pathway ?? null;
    case "progress":
      return row.moveIn ? row.moveIn.complete / Math.max(1, row.moveIn.total) : null;
    case "overdue":
      return row.moveIn?.overdue ?? 0;
    default:
      return new Date(row.admissionDate).getTime();
  }
}
